import { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import {
  Plane, MapPin, Gauge, Battery, Navigation, Wind, Satellite, Compass,
  ArrowUp, ArrowDown, Home, OctagonX, Shield, Radio, RefreshCw,
  ChevronUp, ChevronDown, AlertTriangle, CheckCircle2, Clock, Wifi, WifiOff,
  RotateCcw, Crosshair, Activity, Thermometer, Eye, Box,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Drone icon for map
const droneIcon = new L.DivIcon({
  html: `<div style="width:36px;height:36px;display:flex;align-items:center;justify-content:center;background:#0f172a;border:2px solid #00ccff;border-radius:50%;box-shadow:0 0 16px rgba(0,204,255,0.6)"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00ccff" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></div>`,
  className: '', iconSize: [36, 36], iconAnchor: [18, 18],
});

const targetIcon = new L.DivIcon({
  html: `<div style="width:28px;height:28px;display:flex;align-items:center;justify-content:center;background:#dc2626;border:2px solid #fff;border-radius:50%;box-shadow:0 0 12px rgba(220,38,38,0.5)"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/></svg></div>`,
  className: '', iconSize: [28, 28], iconAnchor: [14, 14],
});

const FLIGHT_MODES = {
  0: 'Idle', 1: 'Takeoff', 2: 'Landing', 3: 'Motor Start',
  4: 'Landing', 5: 'Hover', 6: 'Mission', 7: 'RTH',
  8: 'Ascending', 9: 'Orbit', 10: 'FPV', 11: 'GPS Follow',
  12: 'DRC', 14: 'Wayline', 15: 'Emergency Stop',
  'N/A': 'Sem dados',
};

const MapUpdater = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center[0] !== 0 && center[1] !== 0) map.setView(center, map.getZoom(), { animate: true });
  }, [center, map]);
  return null;
};

const MapClickHandler = ({ onMapClick, enabled }) => {
  useMapEvents({
    click: (e) => {
      if (enabled) onMapClick(e.latlng);
    },
  });
  return null;
};

const StatCard = ({ icon: Icon, label, value, unit, color = 'text-slate-800', warning }) => (
  <div className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition-all ${warning ? 'bg-red-50 border-red-200' : 'bg-white border-slate-100'}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0 ${warning ? 'bg-red-100' : 'bg-slate-50'}`}>
      <Icon size={14} className={warning ? 'text-red-500' : 'text-amber-500'} />
    </div>
    <div className="min-w-0">
      <p className="text-slate-400 text-[9px] font-mono uppercase tracking-wider">{label}</p>
      <p className={`font-semibold text-sm leading-tight ${color}`}>{value}<span className="text-slate-400 font-normal text-xs ml-0.5">{unit}</span></p>
    </div>
  </div>
);

const CommandButton = ({ icon: Icon, label, onClick, disabled, variant = 'default', loading, testId }) => {
  const variants = {
    default: 'bg-slate-800 hover:bg-slate-700 text-white',
    success: 'bg-emerald-600 hover:bg-emerald-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    warning: 'bg-amber-500 hover:bg-amber-600 text-white',
    outline: 'bg-white hover:bg-slate-50 text-slate-700 border border-slate-200',
  };
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-mono text-xs tracking-wider transition-all disabled:opacity-40 disabled:cursor-not-allowed ${variants[variant]}`}
      data-testid={testId}
    >
      {loading ? <RefreshCw size={14} className="animate-spin" /> : <Icon size={14} />}
      {label}
    </button>
  );
};

const DroneControlPage = () => {
  const [telemetry, setTelemetry] = useState(null);
  const [droneStatus, setDroneStatus] = useState(null);
  const [commandLog, setCommandLog] = useState([]);
  const [sendingCmd, setSendingCmd] = useState(null);
  const [targetPoint, setTargetPoint] = useState(null);
  const [selectingTarget, setSelectingTarget] = useState(false);
  const [takeoffAlt, setTakeoffAlt] = useState(50);
  const [showLog, setShowLog] = useState(false);
  const wsRef = useRef(null);

  // WebSocket for telemetry
  const connectWs = useCallback(() => {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const ws = new WebSocket(`${protocol}//${window.location.host}/api/ws/telemetry`);
    ws.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'telemetry') setTelemetry(msg.data);
        if (msg.type === 'command_reply') {
          const { method, result } = msg.data;
          if (result === 0) {
            toast.success(`Comando ${method} executado com sucesso`);
          } else {
            toast.error(`Comando ${method} falhou (code: ${result})`);
          }
          fetchCommandLog();
        }
      } catch {}
    };
    ws.onclose = () => setTimeout(connectWs, 5000);
    ws.onerror = () => ws.close();
    wsRef.current = ws;
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await api.get('/drone/status');
      setDroneStatus(res.data);
    } catch {}
  };

  const fetchCommandLog = async () => {
    try {
      const res = await api.get('/drone/command/log');
      setCommandLog(res.data.log || []);
    } catch {}
  };

  useEffect(() => {
    connectWs();
    fetchStatus();
    fetchCommandLog();
    const interval = setInterval(fetchStatus, 10000);
    return () => {
      clearInterval(interval);
      if (wsRef.current) wsRef.current.close();
    };
  }, [connectWs]);

  const sendCommand = async (command, params = null) => {
    setSendingCmd(command);
    try {
      await api.post('/drone/command', { command, params });
      toast.info(`Comando ${command} enviado`);
      fetchCommandLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Erro: ${command}`);
    } finally {
      setSendingCmd(null);
    }
  };

  const handleTakeoff = () => {
    const params = { target_height: takeoffAlt };
    if (targetPoint) {
      params.target_latitude = targetPoint.lat;
      params.target_longitude = targetPoint.lng;
    }
    sendCommand('takeoff_to_point', params);
  };

  const handleMapClick = (latlng) => {
    setTargetPoint(latlng);
    setSelectingTarget(false);
    toast.success(`Ponto alvo: ${latlng.lat.toFixed(6)}, ${latlng.lng.toFixed(6)}`);
  };

  const mqttOk = droneStatus?.mqtt_connected;
  const hasPosition = telemetry && telemetry.latitude !== 0 && telemetry.longitude !== 0;
  const mapCenter = hasPosition ? [telemetry.latitude, telemetry.longitude] : [-10.96, -38.13];
  const flightModeLabel = telemetry ? (FLIGHT_MODES[telemetry.flight_mode] || `Mode ${telemetry.flight_mode}`) : 'Sem dados';
  const lowBattery = telemetry?.battery > 0 && telemetry?.battery < 20;

  return (
    <div className="space-y-4" data-testid="drone-control-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-xl font-semibold text-slate-800 uppercase tracking-wider flex items-center gap-2" data-testid="page-title">
          <Plane size={20} className="text-amber-500" /> CONTROLE DO DRONE
        </h2>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border ${mqttOk ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-red-50 text-red-500 border-red-200'}`} data-testid="mqtt-status">
            {mqttOk ? <Wifi size={12} /> : <WifiOff size={12} />}
            MQTT {mqttOk ? 'CONECTADO' : 'OFFLINE'}
          </span>
          <span className={`inline-flex items-center gap-1.5 text-xs font-mono px-3 py-1.5 rounded-full border ${telemetry ? 'bg-blue-50 text-blue-600 border-blue-200' : 'bg-slate-100 text-slate-400 border-slate-200'}`} data-testid="telemetry-status">
            <Activity size={12} /> {telemetry ? 'TELEMETRIA ATIVA' : 'AGUARDANDO'}
          </span>
          <Button onClick={() => setShowLog(!showLog)} variant="outline" className="h-8 px-3 text-xs font-mono" data-testid="toggle-log-btn">
            <Clock size={12} className="mr-1" /> LOG
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: Map */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200" style={{ height: '400px' }} data-testid="control-map">
            <MapContainer center={mapCenter} zoom={15} style={{ height: '100%', width: '100%' }} zoomControl={true}>
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Esri Satellite"
              />
              <MapUpdater center={mapCenter} />
              <MapClickHandler onMapClick={handleMapClick} enabled={selectingTarget} />
              {hasPosition && (
                <Marker position={[telemetry.latitude, telemetry.longitude]} icon={droneIcon}>
                  <Popup>
                    <strong>Matrice 3TD</strong><br/>
                    Alt: {telemetry.altitude?.toFixed?.(1)}m | Bat: {telemetry.battery}%<br/>
                    Mode: {flightModeLabel}
                  </Popup>
                </Marker>
              )}
              {targetPoint && (
                <Marker position={[targetPoint.lat, targetPoint.lng]} icon={targetIcon}>
                  <Popup>
                    <strong>Ponto Alvo</strong><br/>
                    {targetPoint.lat.toFixed(6)}, {targetPoint.lng.toFixed(6)}
                  </Popup>
                </Marker>
              )}
            </MapContainer>
          </div>

          {/* Flight Controls */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="flight-controls">
            <h3 className="text-slate-800 font-semibold text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Shield size={14} className="text-amber-500" /> COMANDOS DE VOO
            </h3>

            {/* Takeoff altitude + target selection */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
              <div>
                <label className="text-slate-400 text-[10px] font-mono uppercase block mb-1">Altitude (m)</label>
                <div className="flex items-center gap-1">
                  <button onClick={() => setTakeoffAlt(Math.max(10, takeoffAlt - 10))} className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="alt-decrease">
                    <ChevronDown size={14} />
                  </button>
                  <input type="number" value={takeoffAlt} onChange={e => setTakeoffAlt(Number(e.target.value))}
                    className="w-16 h-7 text-center text-sm font-mono border border-slate-200 rounded" data-testid="alt-input" />
                  <button onClick={() => setTakeoffAlt(Math.min(500, takeoffAlt + 10))} className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center" data-testid="alt-increase">
                    <ChevronUp size={14} />
                  </button>
                </div>
              </div>
              <CommandButton icon={Crosshair} label={selectingTarget ? 'CLIQUE NO MAPA' : targetPoint ? `ALVO: ${targetPoint.lat.toFixed(4)}, ${targetPoint.lng.toFixed(4)}` : 'SELECIONAR ALVO'}
                onClick={() => { setSelectingTarget(!selectingTarget); if (!selectingTarget) toast.info('Clique no mapa para selecionar o ponto alvo'); }}
                variant={selectingTarget ? 'warning' : targetPoint ? 'success' : 'outline'}
                testId="select-target-btn" />
              {targetPoint && (
                <button onClick={() => { setTargetPoint(null); toast.info('Alvo removido'); }} className="text-xs text-red-500 hover:text-red-700 font-mono" data-testid="clear-target-btn">Limpar</button>
              )}
            </div>

            {/* Command buttons */}
            <div className="flex flex-wrap gap-2">
              <CommandButton icon={Shield} label="AUTORIDADE" onClick={() => sendCommand('flight_authority_grab')}
                disabled={!mqttOk} loading={sendingCmd === 'flight_authority_grab'} variant="default" testId="cmd-authority" />
              <CommandButton icon={ArrowUp} label="DECOLAR" onClick={handleTakeoff}
                disabled={!mqttOk} loading={sendingCmd === 'takeoff_to_point'} variant="success" testId="cmd-takeoff" />
              <CommandButton icon={Home} label="RETORNAR" onClick={() => sendCommand('return_home')}
                disabled={!mqttOk} loading={sendingCmd === 'return_home'} variant="warning" testId="cmd-rth" />
              <CommandButton icon={ArrowDown} label="POUSAR" onClick={() => sendCommand('land')}
                disabled={!mqttOk} loading={sendingCmd === 'land'} variant="outline" testId="cmd-land" />
              <CommandButton icon={OctagonX} label="EMERGENCIA" onClick={() => { if (window.confirm('Parada de emergencia? O drone vai parar imediatamente.')) sendCommand('hover'); }}
                disabled={!mqttOk} loading={sendingCmd === 'hover'} variant="danger" testId="cmd-emergency" />
            </div>

            {!mqttOk && (
              <div className="mt-3 flex items-center gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-2" data-testid="mqtt-warning">
                <AlertTriangle size={14} /> MQTT desconectado. Os comandos precisam de conexao MQTT ativa.
              </div>
            )}
          </div>
        </div>

        {/* Right: Telemetry */}
        <div className="space-y-4">
          {/* Flight mode */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="flight-mode-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-slate-800 font-semibold text-xs uppercase tracking-wider flex items-center gap-1.5">
                <Activity size={14} className="text-amber-500" /> MODO DE VOO
              </h3>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-testid="flight-mode-value">
                {flightModeLabel}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-1.5 text-[10px] font-mono text-slate-500">
              <div>Dock: <span className="text-slate-700">{droneStatus?.dock_sn || '--'}</span></div>
              <div>Drone: <span className="text-slate-700">{droneStatus?.drone_sn || '--'}</span></div>
            </div>
          </div>

          {/* Telemetry grid */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="telemetry-panel">
            <h3 className="text-slate-800 font-semibold text-xs uppercase tracking-wider mb-3 flex items-center gap-1.5">
              <Gauge size={14} className="text-amber-500" /> TELEMETRIA
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <StatCard icon={MapPin} label="Latitude" value={telemetry?.latitude?.toFixed?.(6) || '--'} unit="" />
              <StatCard icon={MapPin} label="Longitude" value={telemetry?.longitude?.toFixed?.(6) || '--'} unit="" />
              <StatCard icon={Navigation} label="Altitude" value={telemetry?.altitude?.toFixed?.(1) || '--'} unit="m" />
              <StatCard icon={Gauge} label="Velocidade" value={telemetry?.speed?.toFixed?.(1) || '--'} unit="m/s" />
              <StatCard icon={ArrowUp} label="V. Vertical" value={telemetry?.vertical_speed?.toFixed?.(1) || '--'} unit="m/s" />
              <StatCard icon={Battery} label="Bateria" value={telemetry?.battery || '--'} unit="%" warning={lowBattery} color={lowBattery ? 'text-red-600' : 'text-slate-800'} />
              <StatCard icon={Compass} label="Heading" value={telemetry?.heading?.toFixed?.(0) || '--'} unit={'\u00B0'} />
              <StatCard icon={Satellite} label="Satelites" value={telemetry?.satellites || '--'} unit="" />
              <StatCard icon={Wind} label="Vento" value={telemetry?.wind_speed?.toFixed?.(1) || '--'} unit="m/s" />
              <StatCard icon={Home} label="Dist. Home" value={telemetry?.home_distance?.toFixed?.(0) || '--'} unit="m" />
              <StatCard icon={Eye} label="Pitch" value={telemetry?.pitch?.toFixed?.(1) || '--'} unit={'\u00B0'} />
              <StatCard icon={RotateCcw} label="Roll" value={telemetry?.roll?.toFixed?.(1) || '--'} unit={'\u00B0'} />
            </div>
          </div>

          {/* Dock info */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="dock-info">
            <h3 className="text-slate-800 font-semibold text-xs uppercase tracking-wider mb-2 flex items-center gap-1.5">
              <Box size={14} className="text-amber-500" /> DOCK 2
            </h3>
            <div className="grid grid-cols-2 gap-2 text-[10px] font-mono">
              <div className="p-2 bg-slate-50 rounded-md">
                <span className="text-slate-400">SN</span>
                <p className="text-slate-700">{droneStatus?.dock_sn || '--'}</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-md">
                <span className="text-slate-400">MQTT</span>
                <p className={mqttOk ? 'text-emerald-600' : 'text-red-500'}>{mqttOk ? 'Online' : 'Offline'}</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-md">
                <span className="text-slate-400">Stream</span>
                <p className={droneStatus?.stream_active ? 'text-emerald-600' : 'text-slate-500'}>{droneStatus?.stream_active ? 'Ativo' : 'Inativo'}</p>
              </div>
              <div className="p-2 bg-slate-50 rounded-md">
                <span className="text-slate-400">Comandos</span>
                <p className="text-slate-700">{droneStatus?.pending_commands || 0} pendentes</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Command log */}
      {showLog && (
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="command-log">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-slate-800 font-semibold text-xs uppercase tracking-wider flex items-center gap-1.5">
              <Clock size={14} className="text-amber-500" /> LOG DE COMANDOS
            </h3>
            <Button onClick={fetchCommandLog} variant="outline" className="h-7 px-2 text-xs" data-testid="refresh-log-btn">
              <RefreshCw size={12} />
            </Button>
          </div>
          {commandLog.length === 0 ? (
            <p className="text-slate-400 text-xs font-mono text-center py-4">Nenhum comando enviado</p>
          ) : (
            <div className="max-h-60 overflow-y-auto space-y-1.5">
              {commandLog.map((entry, i) => (
                <div key={i} className="flex items-center gap-2 text-[11px] font-mono p-2 rounded-md bg-slate-50 border border-slate-100" data-testid={`log-entry-${i}`}>
                  {entry.status === 'sent' ? (
                    <Clock size={12} className="text-amber-500 flex-shrink-0" />
                  ) : entry.status === 'success' ? (
                    <CheckCircle2 size={12} className="text-emerald-500 flex-shrink-0" />
                  ) : (
                    <AlertTriangle size={12} className="text-red-500 flex-shrink-0" />
                  )}
                  <span className="text-slate-700 font-medium">{entry.method}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[9px] ${entry.status === 'sent' ? 'bg-amber-100 text-amber-700' : entry.status === 'success' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {entry.status?.toUpperCase()}
                  </span>
                  <span className="text-slate-400 ml-auto">{entry.sent_at?.split('T')[1]?.slice(0, 8) || ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DroneControlPage;
